/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import NoImage from '../../../images/no_image.jpg';
import { classNames } from '../../../utils/classnames';
import { convertHTMLStringToPlainString } from '../../../utils/convertValues';
import { AddToCartButton } from '../../button/add-to-cart';
import { LoadingIcon } from '../../common/LoadingIcon';
import { ProductColorSwatch } from '../color-swatch';
import { useScreen } from '../../../hooks/useScreen';
import { NewSelect } from '../../select/new-select';
import {
  GREATER_SELECTED_QUANTITY,
  ITEM_OUT_OF_STOCK,
  PURCHASING_DISABLED
} from '../../../helpers/item-variants-unavailable-texts';
import BreadPayPlacement from '../../../components/breadpay/placement';
import { PRODUCTS_PAGE } from '../../../constants/global';
import { NonBedPdpContext } from '../../template/product/detail/pdp-context';

/**
 * @description Render memoized product view item component
 * @param {Object} item
 * @returns {JSX.Element} Memoized product view item component
 */
export const ProductViewItem = memo(function MemoizedProductViewItem({ productIndex, product, className }) {
  const { setCompleteTheLookListingItem } = useContext(NonBedPdpContext);
  const [loading, setLoading] = useState(false);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedOption, setSelectedOption] = useState([]);
  const [selectedQuantity, setQuantity] = useState(1);
  const [isSSR, setIsSSR] = useState(true);
  const { isMobile } = useScreen();
  let colorOption = null;
  let dropdownOptions = null;

  // const [selectedModifier, setSelectedModifier] = useState([]);

  // const optionCount = product?.options?.length;

  useEffect(() => {
    setIsSSR(false);
  }, []);

  let options = [];
  product?.options?.forEach((item) => {
    const option = { name: item.display_name, option_id: item.id, values: [...item.option_values], type: item.type };
    if (item.display_name.includes('COLOR') || item.display_name.includes('Color')) {
      options.unshift(option);
    } else {
      options.push(option);
    }
  });
  colorOption = options.find((item) => item?.option_id && item?.type === 'swatch');
  dropdownOptions = options.filter((item) => item?.option_id && item?.type !== 'swatch');

  const onSelectOption = (option_id, id) => {
    try {
      let options = [...selectedOption];
      let pos = options.findIndex((item) => {
        return item.option_id === option_id;
      });

      if (pos >= 0) {
        options[pos] = { id, option_id };
      } else options.push({ id, option_id });

      if (options?.length > 0) {
        options.sort((a, b) => a.option_id - b.option_id);
        setSelectedOption([...options]);
      }
    } catch (error) {
      return;
    }
  };

  const getSelectedVariant = () => {
    const variants =
      product?.variants?.filter((item) => {
        const sortedVariant = item?.option_values?.sort((a, b) => a.option_id - b.option_id);
        return JSON.stringify(sortedVariant) === JSON.stringify(selectedOption);
      }) || null;
    return variants?.[0] || product?.variants?.[0];
  };

  const selectedVariant = getSelectedVariant();

  const checkAvailabilityOfItemVariant = (className) => {
    function isAnySelectionPending() {
      if (options?.length > 0) {
        return options.length !== selectedOption?.length;
      }
      return false;
    }
    let notAvailableText = '';
    if (selectedVariant?.inventory_level === 0) {
      notAvailableText = ITEM_OUT_OF_STOCK;
    } else if (selectedVariant?.purchasing_disabled === true) {
      notAvailableText = PURCHASING_DISABLED;
    } else if (selectedQuantity > selectedVariant?.inventory_level) {
      notAvailableText = GREATER_SELECTED_QUANTITY;
    }
    if (notAvailableText && !isAnySelectionPending()) {
      return (
        <span className={`available-label mb-4 text-xs ${className}`}>
          *The selected combination is currently unavailable.
        </span>
      );
    }
  };

  const isAddToCartDisabled = () => {
    return (
      selectedVariant === null ||
      selectedVariant?.inventory_level === 0 ||
      selectedVariant?.purchasing_disabled === true ||
      selectedQuantity > selectedVariant?.inventory_level ||
      (options.length > 0 && options.length !== selectedOption?.length)
    );
  };

  const calculatePrice = useMemo(() => {
    let price;
    if (selectedVariant) {
      price = selectedVariant?.calculated_price.toLocaleString(undefined, { minimumFractionDigits: 2 });
    } else {
      price = product?.calculated_price.toLocaleString(undefined, { minimumFractionDigits: 2 });
    }
    return price;
  }, [selectedVariant, product]);

  useEffect(() => {
    setCompleteTheLookListingItem((prevState) => {
      const newState = [...prevState];
      newState[productIndex] = selectedVariant ? { ...selectedVariant } : { ...newState[productIndex] };
      newState[productIndex] = {
        ...newState[productIndex],
        selectedQuantity,
        productIndex: productIndex.toString(),
        price: calculatePrice
      };

      return newState;
    });
  }, [productIndex, calculatePrice, selectedQuantity, selectedVariant]);

  if (isSSR) return null;

  if (isMobile) {
    return (
      <div
        className={classNames(
          'font-brownProTT item-card-top-border flex w-full flex-col gap-4 border-t py-8 md:items-center md:justify-center lg:flex-row',
          className
        )}
      >
        <div className="flex w-full flex-col gap-3 px-2">
          <div className="item-card-image-container flex gap-2">
            <img
              className="item-card-image w-1/2 md:w-1/5 lg:w-full"
              alt={product?.primary_image?.description || ''}
              loading="lazy"
              src={selectedVariant?.image_url || product?.primary_image?.url_thumbnail || NoImage}
            />

            <div className="w-1/2 md:w-4/5 lg:hidden">
              <p className="mb-2 line-clamp-4 break-words text-xl font-normal uppercase md:mb-5 md:text-2xl">
                {product?.name}
              </p>
              <p className="product-card-price mb-2">
                $
                {selectedVariant
                  ? selectedVariant?.calculated_price.toLocaleString(undefined, {
                      minimumFractionDigits: 2
                    })
                  : product?.calculated_price.toLocaleString(undefined, {
                      minimumFractionDigits: 2
                    })}
              </p>
              <p className="mb-2 line-clamp-2 text-xs font-normal md:mb-5 md:text-sm">
                {product?.meta_description ||
                  convertHTMLStringToPlainString(product?.description).slice(0, 100) + '...'}
              </p>
              <Link to={product?.custom_url?.url} className="flex items-center text-xs md:text-sm">
                <span className="btn btn--secondary !mt-0 text-[11px]">VIEW MORE</span>
              </Link>
            </div>
          </div>
          <div className="w-full lg:w-1/5">
            <div className="flex gap-5">
              <div className="lg:ck hidden w-full flex-1 lg:w-1/5 ">
                <p className="product-card-title mb-2">{product?.name}</p>
                <p className="product-card-price mb-2">
                  $
                  {selectedVariant
                    ? selectedVariant?.calculated_price.toLocaleString(undefined, { minimumFractionDigits: 2 })
                    : product?.calculated_price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
                <p className="mb-2 text-xs font-normal md:mb-5 md:text-sm">
                  {product?.meta_description ||
                    convertHTMLStringToPlainString(product?.description).slice(0, 100) + '...'}
                </p>
                <Link
                  to={
                    product?.custom_url?.url.split('/').includes('products')
                      ? product?.custom_url?.url
                      : `/products${product?.custom_url?.url}`
                  }
                  className="flex items-center text-xs md:mb-5 md:text-sm"
                >
                  <span className="btn btn--secondary text-[11px]">VIEW MORE</span>{' '}
                </Link>
                {/* <p className="product-card-custom-button text-base text-black md:mb-5">Need it custom made?</p> */}
                <Link to="/stores" className="flex items-center text-xs md:text-sm ">
                  <span className="btn btn--secondary text-[11px]">FIND A STORE</span>{' '}
                </Link>
              </div>
            </div>
          </div>
          <div className="w-full lg:w-3/5">
            <div className="flex gap-5">
              <div className="w-full lg:w-3/5">
                <div className="d-flex">
                  <div className={classNames(`product-card mt-5 flex flex-wrap gap-y-1`)}>
                    {options.length > 0 ? (
                      <div
                        className={`${
                          !(options.length % 2) ? 'product-card-even-options' : 'product-card-odd-options'
                        }${colorOption ? '-with-color' : '-without-color'}--mob ${
                          options.length === 1 ? 'product-card-odd-option-single' : 'w-full'
                        }`}
                      >
                        {options.length ? (
                          <div className={`product-card-option-list d-flex`}>
                            {colorOption && (
                              <div className={`product-card-option-color--mob -mt-1`}>
                                {selectedColor ? (
                                  <p className="product-card-selector mb-2 flex items-center  text-xs font-normal leading-5 ">
                                    Color:{' '}
                                    <span className="lowercase text-black first-letter:!uppercase">
                                      {colorOption?.values?.find((value) => value.id === selectedColor)?.label}
                                    </span>
                                  </p>
                                ) : (
                                  <p className="mb-1 block text-xs font-normal lowercase leading-5 first-letter:!uppercase">
                                    SELECT {colorOption.name}
                                  </p>
                                )}
                                <ProductColorSwatch
                                  handleChange={(id) => {
                                    setSelectedColor(id);
                                    onSelectOption(colorOption.option_id, id);
                                  }}
                                  selected={selectedColor}
                                  colors={colorOption?.values}
                                  options={options}
                                  product={product}
                                  selectedVariant={selectedVariant}
                                  selectedOption={selectedOption}
                                />
                              </div>
                            )}

                            {!(dropdownOptions.length % 2)
                              ? dropdownOptions.map((item, index) => (
                                  <div
                                    className={classNames('px-px-0 ', 'product-card-option')}
                                    data-name={item.name}
                                    key={index}
                                  >
                                    <NewSelect
                                      name={item.name}
                                      options={item?.values}
                                      className="product-card-selector h-10 w-full border"
                                      handleChange={({ value: id }) => {
                                        onSelectOption(item.option_id, Number(id));
                                      }}
                                    />
                                  </div>
                                ))
                              : dropdownOptions?.map((item, index) => (
                                  <div
                                    className={classNames('px-px-0', 'product-card-option')}
                                    data-name={item.name}
                                    key={index}
                                  >
                                    <NewSelect
                                      name={item.name}
                                      options={item?.values}
                                      className="product-card-selector h-10 w-full border"
                                      handleChange={({ value: id }) => {
                                        onSelectOption(item.option_id, Number(id));
                                      }}
                                    />
                                  </div>
                                ))}

                            {!!options.length && !!(options.length % 2) && (
                              <div className={`product-card-row product-card-row-even--mob`}>
                                <div className="product-card-button mb-4">
                                  <NewSelect
                                    className="product-card-button-text bed-option product-card-quantity h-11 border"
                                    name="quantity"
                                    options={[...Array(10).keys()]}
                                    selected={selectedQuantity}
                                    handleChange={({ value }) => {
                                      setQuantity(value);
                                    }}
                                  />
                                  <div className="flex flex-col items-center gap-2">
                                    <AddToCartButton
                                      className="product-card-add-to-cart inline-flex h-12 w-full items-center justify-center border bg-black text-white"
                                      variant={selectedVariant}
                                      options={selectedOption}
                                      quantity={selectedQuantity}
                                      disabled={isAddToCartDisabled()}
                                      isCompleteTheLookSection
                                    >
                                      {loading && <LoadingIcon />} Add to cart
                                    </AddToCartButton>
                                    <BreadPayPlacement type={PRODUCTS_PAGE} productIndex={productIndex.toString()} />
                                  </div>
                                </div>
                                {checkAvailabilityOfItemVariant('')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="quantity-block w-full px-0 text-center md:flex md:px-2">
                            {!!options?.length && !!(options.length % 2) && (
                              <div
                                className={classNames('px-0 md:px-2')}
                                data-name={options[options.length - 1].name}
                                key={options.length - 1}
                              >
                                <NewSelect
                                  name={options[options.length - 1].name}
                                  options={options[options.length - 1]?.values}
                                  className="product-card-selector product-card-cart-section-option product-card-option h-10 border"
                                  handleChange={({ value: id }) => {
                                    onSelectOption(options[options.length - 1].option_id, Number(id));
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`product-card-row product-card-row-odd--mob flex w-full flex-col items-center`}>
                        <div className="product-card-buttons mb-4">
                          <NewSelect
                            className="product-card-button-text bed-option product-card-quantity h-11 border"
                            name="quantity"
                            options={[...Array(10).keys()]}
                            selected={selectedQuantity}
                            handleChange={({ value }) => {
                              setQuantity(value);
                            }}
                          />
                          <div className="w-1/2">
                            <AddToCartButton
                              className="product-card-add-to-cart inline-flex h-12 w-full items-center justify-center border bg-black text-white lg:text-base "
                              variant={selectedVariant}
                              options={selectedOption}
                              quantity={selectedQuantity}
                              disabled={isAddToCartDisabled()}
                              isCompleteTheLookSection
                            >
                              {loading && <LoadingIcon />} Add to cart
                            </AddToCartButton>
                          </div>
                        </div>
                        <BreadPayPlacement type={PRODUCTS_PAGE} productIndex={productIndex.toString()} />
                        {checkAvailabilityOfItemVariant('')}
                      </div>
                    )}
                  </div>
                </div>
                {/* working */}
                {!!options?.length && !(options.length % 2) && (
                  <div className={`product-card-row product-card-row-odd--mob flex flex-col items-center`}>
                    <div className="product-card-buttons mb-4 gap-2">
                      <NewSelect
                        className="product-card-button-text bed-option product-card-quantity h-11 border"
                        name="quantity"
                        options={[...Array(10).keys()]}
                        selected={selectedQuantity}
                        handleChange={({ value }) => {
                          setQuantity(value);
                        }}
                      />
                      <div className="flex w-1/2 flex-col items-center justify-center gap-2">
                        <AddToCartButton
                          className="product-card-add-to-cart inline-flex h-12 w-full items-center justify-center border bg-black text-white lg:text-base"
                          variant={selectedVariant}
                          options={selectedOption}
                          quantity={selectedQuantity}
                          disabled={isAddToCartDisabled()}
                          isCompleteTheLookSection
                        >
                          {loading && <LoadingIcon />} Add to cart
                        </AddToCartButton>
                      </div>
                    </div>
                    <div>
                      <BreadPayPlacement type={PRODUCTS_PAGE} productIndex={productIndex.toString()} />
                    </div>

                    {checkAvailabilityOfItemVariant('')}
                  </div>
                )}

                {/* <div className="wish-button ml-auto px-0 text-center md:w-1/3 md:px-2" onClick={addToWishlist}>
                  <button className="product-card-button-text inline-flex h-10 w-full items-center justify-center border bg-white">
                    Add to wishlist
                  </button>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        'font-brownProTT item-card-top-border flex w-full flex-col gap-4 border-t py-8 md:items-center md:justify-center lg:flex-row',
        className
      )}
    >
      <div className="flex w-full flex-col gap-2 px-2 lg:flex-row lg:gap-8 lg:px-0">
        <div className="item-card-image-container flex gap-5">
          <img
            className="item-card-image w-1/2 md:w-1/5 lg:w-full"
            alt={product?.primary_image?.description || ''}
            loading="lazy"
            src={selectedVariant?.image_url || product?.primary_image?.url_thumbnail || NoImage}
          />

          <div className="w-3/5 md:w-4/5 lg:hidden">
            <p className="mb-2 text-xl font-normal uppercase md:mb-5 md:text-2xl">{product?.name}</p>
            <p className="mb-2 text-xs font-normal md:mb-5 md:text-sm">
              {product?.meta_description || convertHTMLStringToPlainString(product?.description).slice(0, 100) + '...'}
            </p>
            <Link to={product?.custom_url?.url} className="flex items-center text-xs md:text-sm ">
              <span className="btn btn--secondary text-[11px]">VIEW MORE</span>
            </Link>
          </div>
        </div>
        <div className="w-full lg:w-1/5">
          <div className="flex gap-5">
            <div className="hidden w-full flex-1 lg:block lg:w-1/5 ">
              <p className="product-card-title mb-2">{product?.name}</p>
              <p className="product-card-price mb-2">${calculatePrice}</p>
              <p className="mb-2 text-xs font-normal md:mb-5 md:text-sm">
                {product?.meta_description ||
                  convertHTMLStringToPlainString(product?.description).slice(0, 100) + '...'}
              </p>
              <Link
                to={
                  product?.custom_url?.url.split('/').includes('products')
                    ? product?.custom_url?.url
                    : `/products${product?.custom_url?.url}`
                }
                className="flex items-center text-xs md:mb-2 md:text-sm"
              >
                <span className="btn btn--secondary mt-0 text-[11px]">VIEW MORE</span>{' '}
              </Link>
              {/* <p className="product-card-custom-button text-base text-black md:mb-5">Need it custom made?</p> */}
              <Link to="/stores" className="flex items-center text-xs md:text-sm ">
                <span className="btn btn--secondary mt-0 text-[11px]">FIND A STORE</span>
              </Link>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-3/5">
          <div className="flex gap-5">
            <div className="w-full flex-1 lg:w-3/5">
              <div className="d-flex" data-empty={dropdownOptions?.length % 2 < 1}>
                <div className={classNames(`product-card mt-5 flex flex-wrap gap-y-1 md:mt-0`)}>
                  {options.length > 0 && (
                    <div
                      className={`${
                        !(dropdownOptions.length % 2) ? 'product-card-even-options' : 'product-card-odd-options'
                      }${colorOption ? '-with-color' : '-without-color'} ${
                        dropdownOptions.length % 2 && !colorOption ? 'w-2/3' : ''
                      } ${dropdownOptions.length === 1 ? 'product-card-odd-option-single !w-1/3' : ''}`}
                    >
                      {colorOption && (
                        <div className={`product-card-color ${dropdownOptions.length === 0 ? 'lg:w-1/2' : 'w-full'}`}>
                          {selectedColor ? (
                            <p className="product-card-selector mb-2 flex items-center  text-xs font-normal leading-5 ">
                              Color:{' '}
                              <span className="lowercase text-black first-letter:!uppercase">
                                {colorOption?.values?.find((value) => value.id === selectedColor)?.label}
                              </span>
                            </p>
                          ) : (
                            <p className="mb-1 block text-xs font-normal lowercase leading-5 first-letter:!uppercase">
                              SELECT {colorOption.name}
                            </p>
                          )}
                          <ProductColorSwatch
                            handleChange={(id) => {
                              setSelectedColor(id);
                              onSelectOption(colorOption.option_id, id);
                            }}
                            selected={selectedColor}
                            colors={colorOption?.values}
                            options={options}
                            product={product}
                            selectedVariant={selectedVariant}
                            selectedOption={selectedOption}
                          />
                        </div>
                      )}
                      {dropdownOptions.length ? (
                        <div
                          className={`product-card-option-list d-flex mb-2 md:px-2 ${
                            dropdownOptions.length % 2 && !colorOption ? '' : 'w-2/3'
                          }`}
                        >
                          {dropdownOptions && !(dropdownOptions.length % 2)
                            ? dropdownOptions.map((item, index) => (
                                <div
                                  className={classNames(
                                    'px-px-0 ',
                                    'product-card-option',
                                    options.length === 1 && '!w-full md:!w-1/2'
                                  )}
                                  data-name={item.name}
                                  key={index}
                                >
                                  <NewSelect
                                    name={item.name}
                                    options={item?.values}
                                    className="product-card-selector h-10 w-full border"
                                    handleChange={({ value: id }) => {
                                      onSelectOption(item.option_id, Number(id));
                                    }}
                                  />
                                </div>
                              ))
                            : dropdownOptions.slice(0, -1)?.map((item, index) => (
                                <div
                                  className={classNames(
                                    'px-px-0',
                                    'product-card-option',
                                    options.length === 1 && '!w-full md:!w-1/2'
                                  )}
                                  data-name={item.name}
                                  key={index}
                                >
                                  <NewSelect
                                    name={item.name}
                                    options={item?.values}
                                    className="product-card-selector h-10 w-full border"
                                    handleChange={({ value: id }) => {
                                      onSelectOption(item.option_id, Number(id));
                                    }}
                                  />
                                </div>
                              ))}
                        </div>
                      ) : (
                        <div className="quantity-block not-one w-2/3 px-0 text-center md:flex md:px-2">
                          {!!dropdownOptions?.length && !!(dropdownOptions.length % 2) && colorOption && (
                            <div
                              className={classNames('px-0 md:px-2', options.length === 1 && '!w-full md:!w-1/2')}
                              data-name={dropdownOptions[dropdownOptions.length - 1].name}
                              key={dropdownOptions.length - 1}
                            >
                              <NewSelect
                                name={dropdownOptions[dropdownOptions.length - 1].name}
                                options={dropdownOptions[dropdownOptions.length - 1]?.values}
                                className="product-card-selector product-card-cart-section-option product-card-option h-10 border"
                                handleChange={({ value: id }) => {
                                  onSelectOption(dropdownOptions[dropdownOptions.length - 1].option_id, Number(id));
                                }}
                              />
                            </div>
                          )}
                          <div
                            className={`product-card-row ${
                              !!dropdownOptions?.length && !!(dropdownOptions.length % 2)
                                ? 'product-card-cart-section-with-option'
                                : 'product-card-cart-section-without-option'
                            } product-card-cart-section product-card-cart${
                              colorOption ? '-with-color' : '-without-color'
                            }`}
                          >
                            <div className="product-card-buttons justify-end">
                              <NewSelect
                                className="product-card-button-text bed-option product-card-quantity h-11 border"
                                name="quantity"
                                options={[...Array(10).keys()]}
                                selected={selectedQuantity}
                                handleChange={({ value }) => {
                                  setQuantity(value);
                                }}
                              />
                              <div className="flex flex-col items-center justify-center">
                                <AddToCartButton
                                  className="product-card-add-to-cart inline-flex h-12 w-full items-center justify-center border bg-black text-white "
                                  variant={selectedVariant}
                                  options={selectedOption}
                                  quantity={selectedQuantity}
                                  disabled={isAddToCartDisabled()}
                                  isCompleteTheLookSection
                                >
                                  {loading && <LoadingIcon />} Add to cart
                                </AddToCartButton>
                                <BreadPayPlacement type={PRODUCTS_PAGE} productIndex={productIndex.toString()} />
                              </div>
                            </div>
                            {checkAvailabilityOfItemVariant('')}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {options.length === 1 && (
                    <>
                      <div className="quantity-block quantity-block-single w-2/3 px-0 text-center md:flex md:px-2 lg:w-2/3">
                        {!!dropdownOptions?.length && !!(dropdownOptions.length % 2) && (
                          <div
                            className={classNames('px-0 md:px-2', options.length === 1 && '!w-full md:!w-1/2')}
                            data-name={dropdownOptions[dropdownOptions.length - 1].name}
                            key={dropdownOptions.length - 1}
                          >
                            <NewSelect
                              name={dropdownOptions[dropdownOptions.length - 1].name}
                              options={dropdownOptions[dropdownOptions.length - 1]?.values}
                              className="product-card-selector product-card-cart-section-option product-card-option h-10 border"
                              handleChange={({ value: id }) => {
                                onSelectOption(dropdownOptions[dropdownOptions.length - 1].option_id, Number(id));
                              }}
                            />
                          </div>
                        )}

                        {!!dropdownOptions?.length && dropdownOptions.length === 1 && (
                          <>
                            <div className="product-card-buttons product-card-single flex flex-wrap">
                              <div className="flex w-full gap-2">
                                <NewSelect
                                  className="product-card-button-text bed-option product-card-quantity h-11 border"
                                  name="quantity"
                                  options={[...Array(10).keys()]}
                                  selected={selectedQuantity}
                                  handleChange={({ value }) => {
                                    setQuantity(value);
                                  }}
                                />
                                <AddToCartButton
                                  className="product-card-add-to-cart inline-flex h-12 w-full items-center justify-center border bg-black text-white "
                                  variant={selectedVariant}
                                  options={selectedOption}
                                  quantity={selectedQuantity}
                                  disabled={isAddToCartDisabled()}
                                  isCompleteTheLookSection
                                >
                                  {loading && <LoadingIcon />} Add to cart
                                </AddToCartButton>
                              </div>
                              <BreadPayPlacement type={PRODUCTS_PAGE} productIndex={productIndex.toString()} />
                              {checkAvailabilityOfItemVariant()}
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                  {options.length !== 1 && (
                    <div
                      className={`product-card-row ml-auto px-0 text-center md:px-2 ${
                        !!dropdownOptions?.length && !!(dropdownOptions.length % 2) ? 'd-flex w-2/3' : 'md:w-1/3'
                      }`}
                    >
                      {!!dropdownOptions?.length && !!(dropdownOptions.length % 2) && (
                        <div
                          className="product-card-option"
                          data-name={dropdownOptions[dropdownOptions.length - 1].name}
                          key={dropdownOptions.length - 1}
                        >
                          <NewSelect
                            name={dropdownOptions[dropdownOptions.length - 1].name}
                            options={dropdownOptions[dropdownOptions.length - 1]?.values}
                            className="product-card-selector product-card-option h-10 w-full border"
                            handleChange={({ value: id }) => {
                              onSelectOption(dropdownOptions[dropdownOptions.length - 1].option_id, Number(id));
                            }}
                          />
                        </div>
                      )}
                      <div
                        className={`${
                          !!dropdownOptions?.length && !!(dropdownOptions.length % 2) && ''
                        } product-card-cart-section`}
                        style={{ width: !!dropdownOptions?.length && !!(dropdownOptions.length % 2) ? '48%' : '100%' }}
                      >
                        <div className="product-card-buttons">
                          <NewSelect
                            // className="product-card-button-text bed-option product-card-quantity h-11 border"
                            className="bed-option product-add-to-cart-quantity w-full"
                            name="quantity"
                            options={[...Array(10).keys()]}
                            selected={selectedQuantity}
                            handleChange={({ value }) => {
                              setQuantity(value);
                            }}
                          />
                          <AddToCartButton
                            className="product-card-add-to-cart inline-flex h-12 w-full items-center justify-center border bg-black text-white"
                            variant={getSelectedVariant()}
                            options={selectedOption}
                            quantity={selectedQuantity}
                            disabled={isAddToCartDisabled()}
                            isCompleteTheLookSection
                          >
                            {loading && <LoadingIcon />} Add to cart
                          </AddToCartButton>
                        </div>
                        <BreadPayPlacement type={PRODUCTS_PAGE} productIndex={productIndex.toString()} />
                        {checkAvailabilityOfItemVariant('')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* <div className="wish-button ml-auto px-0 text-center md:w-1/3 md:px-2" onClick={addToWishlist}>
                <button className="product-card-button-text inline-flex h-10 w-full items-center justify-center border bg-white">
                  Add to wishlist
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ProductViewItem.propTypes = {
  product: PropTypes.shape({
    title: PropTypes.string
  }),
  productIndex: PropTypes.number
};
